import React, { useEffect, useState } from 'react';
import { PlusIcon } from 'lucide-react';
import { usePortal } from '../../contexts/PortalContext';
import { fetchProjectTeam, fetchStaffDirectory, type StaffOption } from '../../utils/projectTeam';
import { ProjectSteps } from './ProjectSteps';
import { TellThem } from './TellThem';
import { BUTTON_GHOST, BUTTON_QUIET, BUTTON_SOLID, FIELD, OPTION, SELECT } from './adminStyles';

/**
 * Who at Portal is responsible for this project. The assignments live in
 * public.project_staff — internal only, and entirely separate from the client
 * access system — so they survive leaving, refreshing, signing out and opening
 * Portal anywhere else.
 *
 * **This file also mounts two sections that belong to the page rather than to
 * the team**, in the order they appear: <TellThem /> (telling the client
 * something is up) and <ProjectSteps /> (the project tracker). On the page
 * they read as two plain sections between Deliverables and Portal Team —
 * exactly where they would sit if they were mounted in
 * pages/portal/ProjectAdmin.tsx directly. They are here because that page is
 * ~14.5KB and the artifact write tool silently truncates a file at that size
 * (three failures on 9 Oct 2026 alone), so editing it at all risks losing it.
 * Nothing about either depends on living in this file: if ProjectAdmin.tsx is
 * ever split up, move the imports and the two lines below into it and delete
 * this paragraph.
 *
 * **<ClientPagePanel /> was mounted here for about an hour on 14 Oct 2026
 * and has been taken back out.** It was the client-page menu built as a flat
 * list of ten tickboxes in 10px type, and Tom, on seeing it: *"this isn't
 * how I want it… I don't want a massive checklist that they have to go
 * through."* He had twice pointed at the Build Your Production flow as the
 * model and this was the opposite of it. The component and
 * `utils/clientPage.ts` are still on disk and the columns still exist; the
 * replacement is a staged flow — pre-production, production, post-production
 * — with one big question per screen. Do not simply re-mount this.
 */
export function PortalTeam() {
  const {
    activeProject,
    setProjectTeam,
    addTeamMember,
    updateTeamMember,
    removeTeamMember
  } = usePortal();
  const [open, setOpen] = useState(false);
  const [staff, setStaff] = useState<StaffOption[]>([]);
  const [userId, setUserId] = useState('');
  const [role, setRole] = useState('');
  const projectId = activeProject.id;
  const slug = activeProject.slug;

  /* The saved assignments, read every time the project is opened. Whatever the
     session held is replaced by exactly what project_staff contains. */
  useEffect(() => {
    let live = true;
    void fetchProjectTeam(projectId, slug).then(team => {
      if (live) setProjectTeam(team);
    });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, slug]);
  useEffect(() => {
    let live = true;
    void fetchStaffDirectory().then(people => {
      if (live) setStaff(people);
    });
    return () => {
      live = false;
    };
  }, []);
  const assignedIds = new Set(activeProject.team.map(member => member.id));
  const available = staff.filter(person => !assignedIds.has(person.userId));
  function submit(event: React.FormEvent) {
    event.preventDefault();
    const person = available.find(candidate => candidate.userId === userId) ?? available[0];
    if (!person) return;
    /* No confirmation here — the assignment is only real once saved. */
    addTeamMember({
      userId: person.userId,
      name: person.name,
      role: role.trim() || 'Crew'
    });
    setUserId('');
    setRole('');
    setOpen(false);
  }
  return <>
      <TellThem data-id="element-1141" data-mp="301urqzs6tj3b" />
      <ProjectSteps data-id="element-1142" data-mp="3009xzcdp646i" />

      <section aria-labelledby="portal-team" className="mt-16" data-id="element-1143" data-mp="10ps0h3dbnkf3">
        <div className="flex flex-wrap items-baseline justify-between gap-4 border-b border-ruleDark pb-4" data-id="element-1144" data-mp="10rl92o7hwepq">
          <h2 id="portal-team" className="label m-0 text-paper/40" data-id="element-1145" data-mp="15qg0gejj3blg">
            Portal Team
          </h2>
          <p className="label-sm m-0 text-paper/35" data-id="element-1146" data-mp="2r6m5k5mega38">Responsibility only · internal</p>
        </div>

        <ul className="m-0 list-none p-0" data-id="element-1147" data-mp="356erzoqw845e">
          {activeProject.team.length === 0 && <li className="border-b border-ruleDark py-8 font-display text-xl text-paper/40" data-id="element-1148" data-mp="3540yhkbv2zzy">
              No team assigned yet.
            </li>}
          {activeProject.team.map(member => <li key={member.id} className="flex flex-wrap items-center justify-between gap-x-8 gap-y-4 border-b border-ruleDark py-6" data-id="element-1149" data-mp="28g4hiecii2o7">
              <p className="min-w-0 flex-1 font-display text-2xl leading-none" data-id="element-1150" data-mp="2m9lxxq9wiza0">{member.name}</p>
              <label className="min-w-[12rem]" data-id="element-1151" data-mp="2hepu7svaynh2">
                <span className="sr-only" data-id="element-1152" data-mp="1tqgdh5cy4pws">Role for {member.name}</span>
                <input value={member.role} onChange={event => updateTeamMember(member.id, {
              role: event.target.value
            })} className={`${FIELD} mt-0`} data-id="element-1153" data-mp="1os54ewuh6jfm" />
              </label>
              <button type="button" onClick={() => removeTeamMember(member.id)} className={BUTTON_QUIET} data-id="element-1154" data-mp="91up37s686fs">
                Remove
              </button>
            </li>)}
        </ul>

        {open ? <form onSubmit={submit} className="mt-8 flex flex-col gap-8 sm:flex-row sm:items-end" data-id="element-1155" data-mp="nxk76uu8jzf8">
            <label className="flex-1" data-id="element-1156" data-mp="s072j5q482h6">
              <span className="label block text-paper/40" data-id="element-1157" data-mp="rz5fe2vtrpj3">Staff Member</span>
              {available.length ? <select value={userId || available[0].userId} onChange={event => setUserId(event.target.value)} autoFocus className={SELECT} data-id="element-1158" data-mp="e20bo9jl32r0">
                  {available.map(person => <option key={person.userId} value={person.userId} className={OPTION} data-id="element-1159" data-mp="33qsue3oj6oq3">
                      {person.name}
                    </option>)}
                </select> : <p className="mt-3 border-b border-paper/25 pb-2 font-sans text-sm tracking-wide2 text-paper/30" data-id="element-1160" data-mp="2fnmilxzyxdzi">
                  {staff.length ? 'Everyone is already assigned' : 'No Portal staff available'}
                </p>}
            </label>
            <label className="flex-1" data-id="element-1161" data-mp="20xot9eqayi8q">
              <span className="label block text-paper/40" data-id="element-1162" data-mp="20u993xhruj0g">Role</span>
              <input value={role} onChange={event => setRole(event.target.value)} placeholder="e.g. Project Lead" className={FIELD} data-id="element-1163" data-mp="1vvzd5ko83eds" />
            </label>
            <div className="flex items-center gap-6" data-id="element-1164" data-mp="1mz78npvp1lv1">
              <button type="submit" disabled={!available.length} className={BUTTON_SOLID} data-id="element-1165" data-mp="3m8ngaoae9eko">
                Assign
              </button>
              <button type="button" onClick={() => setOpen(false)} className={BUTTON_QUIET} data-id="element-1166" data-mp="3h8ssitlx153u">
                Cancel
              </button>
            </div>
          </form> : <button type="button" onClick={() => setOpen(true)} className={`${BUTTON_GHOST} mt-8 inline-flex items-center gap-3`} data-id="element-1167" data-mp="3trn0gw5oja53">
            <PlusIcon className="h-3.5 w-3.5" strokeWidth={1.5} data-id="element-1168" data-mp="7clghc85k4e1" />
            Assign Team Member
          </button>}
      </section>
    </>;
}